import React, { useEffect, useRef, useState } from 'react';
import MarketingNav from '../components/layout/MarketingNav';

const BRAND = {
  forest: '#2A8C8E',
  forestDark: '#237779',
  forestLight: '#5FD0C8',
  sand: '#E6A07C',
  sandLight: '#D9E5E7',
  blue: '#5FD0C8',
  blueLight: '#D9E5E7',
  mint: '#F7FBFA',
  cream: '#F7FBFA',
  warm: '#F7FBFA',
  charcoal: '#2E3A3F',
  slate: '#6E7F86',
  white: '#FFFFFF'
};

const CATEGORIES = [
  { id: 'all', label: 'All References', icon: '📚' },
  { id: 'continuing-care', label: 'Continuing Care & Ongoing Support', icon: '🔄' },
  { id: 'cbt-dbt-act', label: 'CBT, DBT & ACT Modalities', icon: '🧠' },
  { id: 'exercise', label: 'Exercise & Neurobiological Regulation', icon: '🏃' },
  { id: 'journaling', label: 'Journaling & Self-Monitoring', icon: '📝' },
  { id: 'self-efficacy', label: 'Self-Efficacy & Behavioral Activation', icon: '📈' },
  { id: 'faith', label: 'Faith, Spirituality & Wellness', icon: '🕊️' },
  { id: 'digital', label: 'Digital Mental Health Interventions', icon: '📱' }
];

const AUDIENCES = [
  { id: 'all', label: 'All Audiences' },
  { id: 'clinician', label: 'For Clinicians' },
  { id: 'user', label: 'For Users' },
  { id: 'clergy', label: 'For Clergy' }
];

const REFERENCES = [
  {
    id: 1,
    category: 'continuing-care',
    audiences: ['clinician', 'user'],
    authors: 'McKay, J. R.',
    year: 2009,
    title: 'Continuing care research: What we have learned and where we are going',
    journal: 'Journal of Substance Abuse Treatment',
    volume: '36(2)',
    pages: '131–145',
    doi: '10.1016/j.jsat.2008.10.004',
    summary: 'Landmark review demonstrating that extended continuing care interventions (12+ months) produce significantly better substance use outcomes than time-limited treatment alone. Duration and continuity of post-treatment support predicted long-term stability more strongly than initial treatment intensity.',
    relevance: 'Directly supports Momentum\'s 365-day structure and the principle that sustained daily engagement — not just acute treatment — drives lasting mental wellness outcomes.'
  },
  {
    id: 2,
    category: 'continuing-care',
    audiences: ['clinician', 'user'],
    authors: 'Dennis, M. L., & Scott, C. K.',
    year: 2007,
    title: 'Managing mental health as an ongoing wellness practice',
    journal: 'Addiction Science & Clinical Practice',
    volume: '4(1)',
    pages: '45–55',
    doi: '10.1151/ascp074145',
    summary: 'Established the framework for treating mental health as an ongoing wellness practice requiring ongoing management rather than acute episodic intervention. Found that ongoing support services delivered continuously over time significantly improved long-term outcomes across multiple populations.',
    relevance: 'Validates the between-sessions gap that Momentum fills — the hours each week when clients have no clinical contact but face daily mental wellness challenges.'
  },
  {
    id: 3,
    category: 'continuing-care',
    audiences: ['clinician', 'user'],
    authors: 'Jason, L. A., Olson, B. D., Ferrari, J. R., & Lo Sasso, A. T.',
    year: 2006,
    title: 'Community-based settings enhance mental health outcomes',
    journal: 'American Journal of Public Health',
    volume: '96(10)',
    pages: '1727–1729',
    doi: '10.2105/AJPH.2005.070839',
    summary: 'Individuals in Oxford Houses (structured, peer-supported environments) demonstrated 87% sustained wellness outcomes at 2 years compared to approximately 50% for those returning to usual living situations after treatment. Structured accountability was identified as the key differentiating factor.',
    relevance: 'Demonstrates that structured support environments with daily accountability dramatically improve outcomes — the same principle Momentum applies digitally.'
  },
  {
    id: 4,
    category: 'continuing-care',
    audiences: ['clinician'],
    authors: 'White, W. L.',
    year: 2008,
    title: 'Wellness management and wellness-oriented systems of care: Scientific rationale and promising practices',
    journal: 'Northeast Addiction Technology Transfer Center, Great Lakes Addiction Technology Transfer Center, Philadelphia Department of Behavioral Health & Intellectual disAbility Services',
    volume: '',
    pages: '',
    doi: '',
    summary: 'Comprehensive framework establishing long-term management as a paradigm shift from acute treatment models. Documented that sustained support services — including daily structure, peer accountability, and progress monitoring — produce superior long-term outcomes across diverse populations.',
    relevance: 'Provides the theoretical foundation for Momentum\'s ongoing support approach, positioning the platform as infrastructure for long-term management rather than acute treatment.'
  },
  {
    id: 5,
    category: 'cbt-dbt-act',
    audiences: ['clinician', 'user'],
    authors: 'Goldapple, K., Segal, Z., Garson, C., Lau, M., Bieling, P., Kennedy, S., & Mayberg, H.',
    year: 2004,
    title: 'Modulation of cortical-limbic pathways in major depression: Treatment-specific effects of cognitive behavior therapy',
    journal: 'Archives of General Psychiatry',
    volume: '61(1)',
    pages: '34–41',
    doi: '10.1001/archpsyc.61.1.34',
    summary: 'Neuroimaging study demonstrating that CBT produces measurable changes in prefrontal cortex activity and reduces amygdala reactivity — the brain regions governing impulse control and emotional regulation. These neural changes were distinct from those produced by medication, suggesting CBT builds unique cognitive capacities.',
    relevance: 'Supports the Mind pillar\'s daily CBT exercises — each practice strengthens neural pathways for impulse control and emotional regulation through repetition.'
  },
  {
    id: 6,
    category: 'cbt-dbt-act',
    audiences: ['clinician'],
    authors: 'Linehan, M. M., Comtois, K. A., Murray, A. M., Brown, M. Z., Gallop, R. J., Heard, H. L., & Lindenboim, N.',
    year: 2006,
    title: 'Two-year randomized controlled trial and follow-up of dialectical behavior therapy vs therapy by experts for suicidal behaviors and borderline personality disorder',
    journal: 'Archives of General Psychiatry',
    volume: '63(7)',
    pages: '757–766',
    doi: '10.1001/archpsyc.63.7.757',
    summary: 'Randomized controlled trial demonstrating that DBT skills training — distress tolerance, emotion regulation, interpersonal effectiveness, and mindfulness — reduced suicide attempts by 50%, reduced self-harm, and significantly decreased psychiatric hospitalizations compared to expert therapy alone.',
    relevance: 'Validates the inclusion of DBT skill-building exercises in the Mind pillar, particularly distress tolerance techniques critical for mental health wellness.'
  },
  {
    id: 7,
    category: 'cbt-dbt-act',
    audiences: ['clinician', 'user'],
    authors: 'A-Tjak, J. G. L., Davis, M. L., Morina, N., Powers, M. B., Smits, J. A. J., & Emmelkamp, P. M. G.',
    year: 2015,
    title: 'A meta-analysis of the efficacy of acceptance and commitment therapy for clinically relevant mental and physical health problems',
    journal: 'Psychotherapy and Psychosomatics',
    volume: '84(1)',
    pages: '30–36',
    doi: '10.1159/000365764',
    summary: 'Meta-analysis across 39 randomized controlled trials confirming ACT\'s efficacy for depression, anxiety, mental health challenges, and chronic pain. Psychological flexibility — the core ACT mechanism — was identified as a transdiagnostic process that predicts outcomes across diagnostic categories.',
    relevance: 'Supports ACT-based values clarification and psychological flexibility exercises in the Mind pillar, and validates Momentum\'s transdiagnostic approach across mental health challenges, anxiety, depression, and trauma.'
  },
  {
    id: 8,
    category: 'exercise',
    audiences: ['clinician', 'user'],
    authors: 'Blumenthal, J. A., Babyak, M. A., Doraiswamy, P. M., Watkins, L., Hoffman, B. M., Barbour, K. A., & Sherwood, A.',
    year: 2007,
    title: 'Exercise and pharmacotherapy in the treatment of major depressive disorder',
    journal: 'Psychosomatic Medicine',
    volume: '69(7)',
    pages: '587–596',
    doi: '10.1097/PSY.0b013e318148c19a',
    summary: 'Follow-up to the landmark SMILE study confirming that regular aerobic exercise was as effective as sertraline (Zoloft) for major depressive disorder. At 10-month follow-up, the exercise group showed significantly better mental health outcomes than the medication-only group.',
    relevance: 'Directly supports the Move pillar\'s positioning as a clinical intervention — not a fitness program. Exercise is medicine, and daily movement is a neurobiological treatment strategy.'
  }
];

const AUDIENCE_BADGES = {
  clinician: { label: 'Clinician', color: BRAND.blue },
  user: { label: 'User', color: BRAND.forest },
  clergy: { label: 'Clergy', color: BRAND.sand }
};

const fadeIn = {
  opacity: 0,
  transform: 'translateY(20px)'
};

const fadeInActive = {
  opacity: 1,
  transform: 'translateY(0)'
};

const useInView = (threshold = 0.2) => {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setInView(true);
          observer.disconnect();
        }
      },
      { threshold }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [threshold]);

  return [ref, inView];
};

const Pill = ({ label, active, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    style={{
      padding: '8px 16px',
      borderRadius: 999,
      border: active ? `1px solid ${BRAND.forest}` : '1px solid #D9E5E7',
      background: active ? BRAND.forest : BRAND.white,
      color: active ? BRAND.white : BRAND.charcoal,
      fontSize: 13,
      fontWeight: 600,
      transition: 'all 0.2s ease',
      cursor: 'pointer'
    }}
  >
    {label}
  </button>
);

const ResearchCard = ({ reference, delay = 0, inView }) => {
  const audienceBadges = reference.audiences.map((audience) => AUDIENCE_BADGES[audience]);

  return (
    <div
      style={{
        ...fadeIn,
        ...(inView ? fadeInActive : {}),
        transition: `all 0.6s cubic-bezier(0.22, 1, 0.36, 1) ${delay}s`,
        borderRadius: 16,
        border: `1px solid ${BRAND.mint}`,
        background: BRAND.white,
        boxShadow: '0 12px 30px rgba(45,90,71,0.08)',
        padding: '28px 30px',
        display: 'flex',
        flexDirection: 'column',
        gap: 14
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 10 }}>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {audienceBadges.map((badge) => (
            <span
              key={badge.label}
              style={{
                background: `${badge.color}22`,
                color: badge.color,
                borderRadius: 999,
                padding: '4px 10px',
                fontSize: 11,
                fontWeight: 700,
                textTransform: 'uppercase',
                letterSpacing: 0.5
              }}
            >
              {badge.label}
            </span>
          ))}
        </div>
        <span style={{ fontSize: 12, color: BRAND.slate }}>{reference.year}</span>
      </div>
      <div style={{ fontWeight: 700, fontSize: 17, color: BRAND.charcoal }}>{reference.title}</div>
      <div style={{ fontSize: 13, color: BRAND.slate }}>
        {reference.authors} · {reference.journal} {reference.volume && `(${reference.volume})`} {reference.pages}
      </div>
      {reference.doi && (
        <div style={{ fontSize: 12, color: BRAND.blue }}>DOI: {reference.doi}</div>
      )}
      <div style={{ fontSize: 14, color: BRAND.charcoal, lineHeight: 1.6 }}>{reference.summary}</div>
      <div style={{ fontSize: 13, color: BRAND.forestDark, fontStyle: 'italic' }}>{reference.relevance}</div>
    </div>
  );
};

export default function ResearchReferencesPage() {
  const [category, setCategory] = useState('all');
  const [audience, setAudience] = useState('all');
  const [search, setSearch] = useState('');
  const [headerRef, headerInView] = useInView(0.2);
  const [gridRef, gridInView] = useInView(0.2);

  const filteredReferences = REFERENCES.filter((reference) => {
    if (category !== 'all' && reference.category !== category) return false;
    if (audience !== 'all' && !reference.audiences.includes(audience)) return false;
    if (search) {
      const query = search.toLowerCase();
      const haystack = `${reference.title} ${reference.authors} ${reference.journal} ${reference.summary}`.toLowerCase();
      if (!haystack.includes(query)) return false;
    }
    return true;
  });

  return (
    <div
      className="site-canvas"
      style={{
        fontFamily: "'DM Sans', sans-serif",
        color: BRAND.charcoal,
        minHeight: '100vh'
      }}
    >
      <MarketingNav transparent={true} />
      <link
        href="https://fonts.googleapis.com/css2?family=Playfair+Display:wght@400;600;700;800&family=DM+Sans:wght@400;500;600;700&display=swap"
        rel="stylesheet"
      />

      <section className="marketing-photo-hero" style={{ backgroundImage: "url('/Window Moment.png')", color: '#f1fff8' }}>
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10" style={{ maxWidth: '1280px', paddingTop: '130px', paddingBottom: '110px' }}>
        <div
          ref={headerRef}
          className="marketing-photo-hero-panel"
          style={{ ...fadeIn, ...(headerInView ? fadeInActive : {}) }}
        >
          <div
            style={{
              display: 'inline-block',
              background: `${BRAND.sand}30`,
              padding: '8px 20px',
              borderRadius: 999,
              fontSize: 12,
              fontWeight: 700,
              letterSpacing: 2,
              textTransform: 'uppercase',
              border: `1px solid ${BRAND.sand}40`,
              color: BRAND.sandLight,
              marginBottom: 20
            }}
          >
            Research References
          </div>
          <h1
            style={{
              fontFamily: "'Playfair Display', Georgia, serif",
              fontSize: 'clamp(34px, 5vw, 56px)',
              fontWeight: 800,
              marginBottom: 16,
              color: '#ffd166'
            }}
          >
            Evidence & Research Library
          </h1>
          <p style={{ fontSize: 18, lineHeight: 1.7, color: `${BRAND.white}cc` }}>
            A curated collection of research underpinning the MHDF Momentum pillars. Filter by clinical
            focus or audience to support treatment planning and participant education.
          </p>
        </div>
        </div>
      </section>

      <section style={{ padding: '40px 24px 20px' }}>
        <div
          style={{
            maxWidth: 1100,
            margin: '0 auto',
            background: BRAND.white,
            borderRadius: 18,
            padding: '24px',
            boxShadow: '0 10px 30px rgba(45,90,71,0.08)'
          }}
        >
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, marginBottom: 16 }}>
            {CATEGORIES.map((item) => (
              <Pill
                key={item.id}
                label={`${item.icon} ${item.label}`}
                active={category === item.id}
                onClick={() => setCategory(item.id)}
              />
            ))}
          </div>

          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
            {AUDIENCES.map((item) => (
              <Pill
                key={item.id}
                label={item.label}
                active={audience === item.id}
                onClick={() => setAudience(item.id)}
              />
            ))}
            <div style={{ flex: 1, minWidth: 240 }}>
              <input
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder="Search references"
                style={{
                  width: '100%',
                  padding: '10px 14px',
                  borderRadius: 12,
                  border: '1px solid #D9E5E7',
                  fontSize: 14
                }}
              />
            </div>
          </div>
        </div>
      </section>

      <section style={{ padding: '20px 24px 80px' }}>
        <div ref={gridRef} style={{ maxWidth: 1100, margin: '0 auto' }}>
          {filteredReferences.length === 0 && (
            <div
              style={{
                textAlign: 'center',
                padding: '60px 20px',
                color: BRAND.slate,
                background: BRAND.white,
                borderRadius: 16,
                border: `1px solid ${BRAND.mint}`
              }}
            >
              No references found for this filter.
            </div>
          )}
          <div style={{ display: 'grid', gap: 24 }}>
            {filteredReferences.map((reference, index) => (
              <ResearchCard
                key={reference.id}
                reference={reference}
                delay={index * 0.05}
                inView={gridInView}
              />
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
